import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { KeyRound, Loader2, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API, apiError, authHeaders } from "@/lib/api";

const EMPTY = { name: "", username: "", password: "", lista: "" };

const ListaSelect = ({ liste, value, onChange, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testId} className="glass-input h-auto px-5 py-3.5 rounded-2xl border-0 shadow-none data-[placeholder]:text-white/45">
      <SelectValue placeholder="Lista" />
    </SelectTrigger>
    <SelectContent className="bg-black border-white/10 text-white rounded-2xl">
      {liste.map((l) => <SelectItem key={l} value={l} className="rounded-xl focus:bg-white/10 focus:text-white">{l}</SelectItem>)}
    </SelectContent>
  </Select>
);

const PrRow = ({ pr, liste, onChanged }) => {
  const call = async (fn, ok) => {
    try {
      await fn();
      toast.success(ok);
      onChanged();
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  const resetPassword = () => {
    const password = window.prompt(`Nuova password per ${pr.name} (almeno 6 caratteri):`);
    if (password) call(() => axios.put(`${API}/admin/prs/${pr.id}`, { password }, { headers: authHeaders() }), "Password aggiornata");
  };
  const remove = () => {
    if (window.confirm(`Eliminare l'accesso di ${pr.name}?`)) call(() => axios.delete(`${API}/admin/prs/${pr.id}`, { headers: authHeaders() }), "Accesso eliminato");
  };
  return (
    <div className="glass rounded-2xl px-5 py-3 grid sm:grid-cols-[1fr_1fr_220px_auto] gap-3 items-center" data-testid={`pr-row-${pr.username}`}>
      <span className="text-white text-sm">{pr.name}</span>
      <span className="text-white/60 text-sm">Utente: <span className="text-white/85">{pr.username}</span></span>
      <ListaSelect liste={liste} value={pr.lista} testId={`pr-lista-${pr.username}`}
        onChange={(lista) => call(() => axios.put(`${API}/admin/prs/${pr.id}`, { lista }, { headers: authHeaders() }), "Lista aggiornata")} />
      <div className="flex gap-1 justify-end">
        <button onClick={resetPassword} aria-label="Cambia password" data-testid={`pr-reset-${pr.username}`} className="p-2 rounded-full text-white/50 hover:text-white hover:bg-white/5"><KeyRound className="w-4 h-4" /></button>
        <button onClick={remove} aria-label="Elimina" data-testid={`pr-delete-${pr.username}`} className="p-2 rounded-full text-white/50 hover:text-red-300 hover:bg-white/5"><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
};

export const PrPanel = ({ liste }) => {
  const [prs, setPrs] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(() => {
    axios.get(`${API}/admin/prs`, { headers: authHeaders() }).then(({ data }) => setPrs(data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.lista) return toast.error("Scegli la lista del PR");
    setSaving(true);
    try {
      await axios.post(`${API}/admin/prs`, form, { headers: authHeaders() });
      toast.success(`Accesso creato: ${form.username.trim().toLowerCase()} / ${form.password}`);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="glass-strong rounded-[1.5rem] p-6 md:p-8 flex flex-col gap-6" data-testid="admin-pr-panel">
      <div>
        <h2 className="font-heading italic text-white text-3xl tracking-[-1px]">Accessi PR</h2>
        <p className="text-xs text-white/50 mt-1">Ogni PR entra da "Area riservata" con il suo nome utente e vede solo le prenotazioni della sua lista. Non può modificare né cancellare nulla.</p>
      </div>
      <form onSubmit={create} className="grid sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_220px_auto] gap-3">
        <input data-testid="pr-name-input" className="glass-input" required placeholder="Nome (es. Iovino)" value={form.name} onChange={(e) => set("name", e.target.value)} />
        <input data-testid="pr-username-input" className="glass-input" required autoCapitalize="none" placeholder="Nome utente (es. iovino)" value={form.username} onChange={(e) => set("username", e.target.value)} />
        <input data-testid="pr-password-input" className="glass-input" required minLength={6} placeholder="Password (min 6)" value={form.password} onChange={(e) => set("password", e.target.value)} />
        <ListaSelect liste={liste} value={form.lista} onChange={(v) => set("lista", v)} testId="pr-lista-select" />
        <button type="submit" disabled={saving} data-testid="pr-create-button"
          className="bg-white text-black rounded-full px-5 py-3 text-sm font-medium flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4" /> Crea</>}
        </button>
      </form>
      <div className="flex flex-col gap-2" data-testid="pr-list">
        {prs.length === 0 && <p className="text-sm text-white/40">Nessun PR ancora.</p>}
        {prs.map((pr) => <PrRow key={pr.id} pr={pr} liste={liste} onChanged={load} />)}
      </div>
    </section>
  );
};
